import { HTMLInputTypeAttribute, InputHTMLAttributes } from "react";
import {
  Controller,
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  FieldPathValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

interface InputTextProps<FieldsType extends FieldValues> {
  type?: HTMLInputTypeAttribute;
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  required?: boolean;
  shouldUnregister?: boolean;
  placeholder?: string;
  requiredMessage?: string;
  defaultValue?: FieldPathValue<FieldsType, Path<FieldsType>>;
}

const InputText = <FieldsType extends FieldValues>({
  placeholder,
  type,
  label,
  name,
  control,
  required,
  shouldUnregister,
  requiredMessage,
  rules,
  inputProps,
  defaultValue,
}: InputTextProps<FieldsType>) => {
  const { t } = useTranslation();
  return (
    <Controller
      name={name}
      shouldUnregister={shouldUnregister}
      rules={{
        required: requiredMessage || (required && "Required"),
        ...(rules || {}),
        validate: (value = "") => {
          value ||= "";

          return (requiredMessage || required) &&
            value.toString().trim().length === 0
            ? "Required"
            : true;
        },
      }}
      defaultValue={defaultValue}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className="relative">
          {label && (
            <label
              className="text-base ml-1 mb-0.5 block"
              htmlFor={field?.name}
            >
              <span>
                {t(label)}
                {(required || requiredMessage) && (
                  <span className="text-red-600 ml-0.5 text-base">*</span>
                )}
              </span>
            </label>
          )}
          <input
            id={field.name}
            {...field}
            {...(inputProps || {})}
            type={type}
            placeholder={t(placeholder || label)}
            className="placeholder:text-gray-500 border-gray-200 rounded-lg w-full px-2.5 py-3 border focus:border-brand-hover
            focus-within:border-brand-hover focus:bg-[#eaf1fa]  transition-all focus:ring-0 focus-visible:ring-0 focus-visible:border-brand-hover  outline-none"
          />
          <p className="text-red-600 font-normal text-sm m-0 p-0 ml-1  bottom-0 left-0 right-0">
            {t(error?.message)}
          </p>
        </div>
      )}
    />
  );
};

export default InputText;
